import type { FilterCondition } from './types';
import { displayConditionValue } from './labels';

/**
 * Lowercase, "i mening"-form av varje filterfält för isEmpty-mallen
 * ("Endast relevant för gårdar utan {label}").
 */
const FIELD_LABELS_LOWERCASE: Record<FilterCondition['field'], string> = {
  productionTypes: 'produktionsgrenar',
  combinatorBusiness: 'kombinatörsverksamhet',
  geographicFactors: 'geografiska skyddsklasser',
  certifications: 'certifieringar',
  controlPrograms: 'anslutna kontrollprogram',
  otherNotifiable: 'anmälningspliktig verksamhet',
  ecoStatus: 'angiven ekostatus',
  animalUnits: 'angivna djurenheter',
};

/**
 * Adjektiv-/frasform för ecoStatus-värden så att mallen "Endast relevant för
 * {fras} gårdar" läser naturligt.
 */
const ECO_STATUS_PHRASES: Record<string, string> = {
  Ekologisk: 'ekologiska gårdar',
  Konventionell: 'konventionella gårdar',
  'Under omställning till ekologisk': 'gårdar under omställning',
  'Både konventionell och ekologisk': 'gårdar med parallellproduktion',
};

/** "ekologiska gårdar" for a known driftsform value, generic phrasing otherwise. */
const ecoStatusPhrase = (value: string): string =>
  ECO_STATUS_PHRASES[value] ?? `gårdar med ekostatus ${value}`;

/**
 * Svensk uppräkning: ['A'] → "A", ['A','B'] → "A eller B",
 * ['A','B','C'] → "A, B eller C".
 */
export function swedishList(values: string[]): string {
  if (values.length === 0) return '';
  if (values.length === 1) return values[0]!;
  const head = values.slice(0, -1).join(', ');
  const tail = values[values.length - 1]!;
  return `${head} eller ${tail}`;
}

/** Lowercase field labels — exported for the admin-facing rule describer. */
export { FIELD_LABELS_LOWERCASE, ECO_STATUS_PHRASES };

/**
 * Generera en automatisk "reason"-text för ett villkor när adminen inte
 * skrivit en egen. Texten visas för lantbrukaren när kravet döljs (i
 * SectionFilterBadge / HiddenRequirementsToggle).
 *
 * Anropas av:
 * - `applyFarmProfileFilter` när `condition.reason` är tom/odefinierad
 * - `FilterConditionRow` som placeholder så adminen ser exakt vad bonden
 *   kommer se utan att skriva något själv
 *
 * @param condition Det aktuella villkoret (field + operator + value).
 * @returns Färdig svensk meningsfragment-text. Aldrig tom sträng.
 */
export function deriveConditionReason(condition: FilterCondition): string {
  const { field, operator } = condition;
  // Show stored values under the same name the profile UI uses ("IP Sigill" →
  // "IP-standarden (Sigill)"), so the farmer never meets two names for one thing.
  const value = Array.isArray(condition.value)
    ? condition.value.map((v) => displayConditionValue(field, v))
    : condition.value;

  if (operator === 'isEmpty') {
    const label = FIELD_LABELS_LOWERCASE[field] ?? field;
    return `Endast relevant för gårdar utan ${label}`;
  }

  // Set operators on driftsform (AG-275): the engine treats the scalar as a
  // one-element set, so phrase the values as kinds of farm, not as a list of
  // things the farm "has".
  if (
    field === 'ecoStatus' &&
    (operator === 'includesAny' || operator === 'excludesAny') &&
    Array.isArray(value) &&
    value.length > 0
  ) {
    const phrases = swedishList((value as string[]).map(ecoStatusPhrase));
    return operator === 'includesAny'
      ? `Endast relevant för ${phrases}`
      : `Gäller inte ${phrases}`;
  }

  if (operator === 'includesAny' && Array.isArray(value) && value.length > 0) {
    return `Endast relevant för gårdar med ${swedishList(value as string[])}`;
  }

  if (operator === 'includesAll' && Array.isArray(value) && value.length > 0) {
    return `Endast relevant för gårdar med alla av ${swedishList(value as string[])}`;
  }

  if (operator === 'excludesAny' && Array.isArray(value) && value.length > 0) {
    return `Gäller inte gårdar med ${swedishList(value as string[])}`;
  }

  if (operator === 'gte' && typeof value === 'number') {
    return `Endast relevant för gårdar med minst ${value} djurenheter`;
  }

  if (operator === 'lte' && typeof value === 'number') {
    return `Endast relevant för gårdar med högst ${value} djurenheter`;
  }

  if (operator === 'eq' && field === 'animalUnits' && typeof value === 'number') {
    return `Endast relevant för gårdar med ${value} djurenheter`;
  }

  if (operator === 'eq' && field === 'ecoStatus' && typeof value === 'string') {
    return `Endast relevant för ${ecoStatusPhrase(value)}`;
  }

  // Fallback: villkoret är ofullständigt (t.ex. tom värdelista). Ge en
  // generell text så bonden aldrig ser en tom badge.
  return 'Detta krav gäller inte för din gård';
}
